import React, { useState, useEffect, useRef, useCallback } from 'react';
import NarbodicaAuth from './narb-auth/src/components/NarbodicaAuth';
import NarbodicaProfileOnboarding from './narb-auth/src/onboarding/NarbodicaProfileOnboarding';
import { AccessibilityToggle, useAccessibilityStore, detectAccessibilityNeeds } from './narb-auth/src/accessibility';
import { API_BASE as API } from './utils/apiBase';
import { HideButton, NoticesPanel, AppealsPanel } from './components/Moderation';
import { ReplyCount, ThreadPanel, DeepBadge, DeepToggle } from './components/Thread';
import { SearchPanel, DigestToggle } from './components/Search';


const CATEGORIES = [
  { id: 'all', icon: '🌏', label: 'All Channels' },
  { id: 'general', icon: '💬', label: 'General' },
  { id: 'sports', icon: '⚽', label: 'Sports' },
  { id: 'cooking', icon: '🍳', label: 'Cooking' },
  { id: 'philosophy', icon: '🧠', label: 'Philosophy' },
  { id: 'faith', icon: '🙏', label: 'Faith' },
  { id: 'parenting', icon: '👶', label: 'Parenting' },
  { id: 'music', icon: '🎵', label: 'Music' },
  { id: 'gaming', icon: '🎮', label: 'Gaming' },
  { id: 'books', icon: '📚', label: 'Books' },
  { id: 'health', icon: '💪', label: 'Health' },
  { id: 'technology', icon: '💻', label: 'Tech' },
  { id: 'local', icon: '📍', label: 'Local' },
  { id: 'support', icon: '💙', label: 'Support' },
  { id: 'creative', icon: '🎨', label: 'Creative' },
  { id: 'careers', icon: '💼', label: 'Careers' },
];

const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(39,39,42,0.5)', color: '#e4e4e7', fontSize: 13, outline: 'none', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box', marginBottom: 10 };

// ── Auth ──────────────────────────────────
// ── Chat Message ──────────────────────────
function ChatMessage({ msg, isOwn, canModerate, headers, onHidden, onOpenThread }) {
  const time = new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (msg.type === 'system') {
    return (
      <div style={{ textAlign: 'center', padding: '4px 0' }}>
        <span style={{ fontSize: 11, color: '#52525b', fontStyle: 'italic' }}>{msg.content}</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', alignItems: 'flex-start' }}>
      <div style={{ width: 32, height: 32, borderRadius: 16, background: 'rgba(20,184,166,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#14b8a6', flexShrink: 0 }}>
        {(msg.user?.name || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: isOwn ? '#14b8a6' : '#d4d4d8' }}>{msg.user?.name || 'Unknown'}</span>
          <span style={{ fontSize: 10, color: '#3f3f46' }}>{time}</span>
          {msg.edited && <span style={{ fontSize: 9, color: '#3f3f46' }}>(edited)</span>}
          {canModerate && !isOwn && <span style={{ marginLeft: 'auto' }}><HideButton msg={msg} headers={headers} onHidden={onHidden} /></span>}
        </div>
        <div style={{ fontSize: 13, color: '#a1a1aa', lineHeight: 1.5, marginTop: 2, wordBreak: 'break-word' }}>{msg.content}</div>
        {msg.reactions?.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
            {msg.reactions.map((r, i) => (
              <span key={i} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 10, background: 'rgba(39,39,42,0.6)', border: '1px solid rgba(255,255,255,0.04)' }}>
                {r.emoji} {r.users?.length || 0}
              </span>
            ))}
          </div>
        )}
        {onOpenThread && <ReplyCount msg={msg} onOpen={(m) => onOpenThread(m._id)} />}
      </div>
    </div>
  );
}

// ── Create Channel Modal ──────────────────
function CreateChannelModal({ token, onCreated, onClose }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('general');
  const [tone, setTone] = useState('casual');
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/signal/channels`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ name: name.trim(), description: desc.trim(), category, tone }),
      });
      const data = await res.json();
      if (data.success) { onCreated(data.data); onClose(); }
    } catch {}
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div style={{ background: '#18181b', borderRadius: 16, padding: 24, width: 380, maxWidth: '90vw', border: '1px solid rgba(255,255,255,0.06)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#d4d4d8', marginBottom: 16 }}>Create a channel</div>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Channel name" style={inputStyle} autoFocus />
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's this channel about?" style={inputStyle} />
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, appearance: 'none' }}>
          {CATEGORIES.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
        </select>
        <select value={tone} onChange={e => setTone(e.target.value)} style={{ ...inputStyle, appearance: 'none' }}>
          <option value="casual">Casual</option>
          <option value="respectful_debate">Respectful debate</option>
          <option value="support">Support group</option>
          <option value="professional">Professional</option>
        </select>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #3f3f46', background: 'transparent', color: '#71717a', fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>Cancel</button>
          <button onClick={create} disabled={loading || !name.trim()} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid rgba(20,184,166,0.3)', background: 'rgba(20,184,166,0.1)', color: '#14b8a6', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif", opacity: !name.trim() ? 0.4 : 1 }}>
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Event Modal (pins to channel) ──
function CreateEventModal({ token, channel, onCreated, onClose }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (!title.trim() || !date) return;
    setLoading(true);
    try {
      const eventDate = new Date(`${date}T${time || '18:00'}`);
      const appId = 'evt_' + Date.now();
      
      // Pin the event to the channel
      const pinRes = await fetch(`${API}/api/signal/channels/${channel.slug}/pin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          type: 'connect_event', appId, title: title.trim(),
          description: `${desc.trim()}${location ? ' — ' + location : ''}`,
          date: eventDate.toISOString(),
          url: `https://connect.narbodica.com.au/events/${appId}`,
        }),
      });
      const pinData = await pinRes.json();
      
      // Also create an AppEvent so Maia and Connect can pick it up
      try {
        // Backend route is POST /api/events/emit with { app, eventType, eventData, priority }
        // (was /api/app-events with another shape - never reached the backend; route-drift check, 25 Oct 2026).
        await fetch(`${API}/api/events/emit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({
            app: 'signal', eventType: 'signal_event_created', priority: 6,
            eventData: { channelSlug: channel.slug, channelName: channel.name, eventTitle: title.trim(), eventDate: eventDate.toISOString(), location, appId },
          }),
        });
      } catch {} // AppEvents endpoint may not exist yet — don't block the pin
      
      if (pinData.success) { onCreated(); onClose(); }
    } catch {}
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div style={{ background: '#18181b', borderRadius: 16, padding: 24, width: 400, maxWidth: '90vw', border: '1px solid rgba(255,255,255,0.06)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 18 }}>📅</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#d4d4d8' }}>Create event</div>
            <div style={{ fontSize: 11, color: '#52525b' }}>This will be pinned in #{channel.name} and created in Connect</div>
          </div>
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Event name" style={inputStyle} autoFocus />
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's happening?" style={inputStyle} />
        <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Where?" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
          <input type="time" value={time} onChange={e => setTime(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #3f3f46', background: 'transparent', color: '#71717a', fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>Cancel</button>
          <button onClick={create} disabled={loading || !title.trim() || !date} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid rgba(20,184,166,0.3)', background: 'rgba(20,184,166,0.1)', color: '#14b8a6', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif", opacity: (!title.trim() || !date) ? 0.4 : 1 }}>
            {loading ? 'Creating...' : 'Create & Pin'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Initiative Modal (pins to channel) ──
function CreateInitiativeModal({ token, channel, onCreated, onClose }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [goal, setGoal] = useState('');
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (!title.trim()) return;
    setLoading(true);
    try {
      const appId = 'init_' + Date.now();
      
      const pinRes = await fetch(`${API}/api/signal/channels/${channel.slug}/pin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          type: 'grassroots_initiative', appId, title: title.trim(),
          description: `${desc.trim()}${goal ? ' — Goal: ' + goal : ''}`,
          url: `https://grassroots.narbodica.com.au/initiatives/${appId}`,
        }),
      });
      const pinData = await pinRes.json();
      
      // AppEvent for Maia and Grassroots
      try {
        await fetch(`${API}/api/events/emit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({
            app: 'signal', eventType: 'signal_initiative_created', priority: 6,
            eventData: { channelSlug: channel.slug, channelName: channel.name, initiativeTitle: title.trim(), goal, appId },
          }),
        });
      } catch {}
      
      if (pinData.success) { onCreated(); onClose(); }
    } catch {}
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div style={{ background: '#18181b', borderRadius: 16, padding: 24, width: 400, maxWidth: '90vw', border: '1px solid rgba(255,255,255,0.06)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 18 }}>✊</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#d4d4d8' }}>Start initiative</div>
            <div style={{ fontSize: 11, color: '#52525b' }}>This will be pinned in #{channel.name} and created in Grassroots</div>
          </div>
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to change?" style={inputStyle} autoFocus />
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Why does this matter?" style={inputStyle} />
        <input value={goal} onChange={e => setGoal(e.target.value)} placeholder="What's the goal? (e.g. 100 supporters, council response)" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #3f3f46', background: 'transparent', color: '#71717a', fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>Cancel</button>
          <button onClick={create} disabled={loading || !title.trim()} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif", opacity: !title.trim() ? 0.4 : 1 }}>
            {loading ? 'Creating...' : 'Start & Pin'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Channel Summary Banner ────────────────
function ChannelSummary({ token, channel }) {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!channel?.slug) return;
    const dismissed_key = 'signal_summary_' + channel.slug;
    if (sessionStorage.getItem(dismissed_key)) { setDismissed(true); return; }
    setDismissed(false); setSummary(null);
    
    fetch(`${API}/api/signal/channels/${channel.slug}/summary`, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.json()).then(data => {
      if (data.success) setSummary(data.data.summary);
    }).catch(() => {});
  }, [channel?.slug, token]);

  if (dismissed || !summary) return null;

  return (
    <div style={{ padding: '10px 20px', background: 'rgba(20,184,166,0.04)', borderBottom: '1px solid rgba(20,184,166,0.1)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>✨</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#14b8a6', marginBottom: 2 }}>What's been happening here</div>
        <div style={{ fontSize: 12, color: '#a1a1aa', lineHeight: 1.5 }}>{summary}</div>
      </div>
      <button onClick={() => { setDismissed(true); sessionStorage.setItem('signal_summary_' + channel.slug, '1'); }}
        style={{ background: 'none', border: 'none', color: '#3f3f46', cursor: 'pointer', fontSize: 14, padding: 4 }}>×</button>
    </div>
  );
}

// ── Main App ──────────────────────────────
export default function App() {
  const [token, setToken] = useState(localStorage.getItem('signal_token'));
  const [user, setUser]   = useState(() => { try { return JSON.parse(localStorage.getItem('signal_user')); } catch { return null; } });

  // Onboarding state — true when user has a token but hasn't completed
  // Signal-specific onboarding yet. Mirrors Connect's pattern (App.js
  // line ~365). Uses NarbodicaProfile/for-app/signal to check completion,
  // so onboarding state is shared across devices via the unified profile.
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Check onboarding status whenever token changes (login, handoff redemption)
  useEffect(() => {
    if (!token) { setOnboardingChecked(true); return; }
    setOnboardingChecked(false);
    fetch(`${API}/api/narbodica-profile/for-app/signal`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && !data.data?.onboardingDone) {
          setNeedsOnboarding(true);
        }
      })
      .catch(() => {}) // network error → don't force onboarding, let user use the app
      .finally(() => setOnboardingChecked(true));
  }, [token]);

  // Cross-app SSO — redeem ?narb_handoff= token from Maia on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get('narb_handoff');
    if (!handoff || token) return; // already logged in, or no handoff

    // Clean the URL immediately
    const clean = new URL(window.location.href);
    clean.searchParams.delete('narb_handoff');
    window.history.replaceState({}, '', clean.toString());

    fetch(`${API}/api/auth/cross-app/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: handoff }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.tokens) {
          localStorage.setItem('signal_token', data.data.tokens.accessToken);
          localStorage.setItem('signal_user', JSON.stringify(data.data.user));
          setToken(data.data.tokens.accessToken);
          setUser(data.data.user);
        }
      })
      .catch(() => {}); // silently fall through to login screen
  }, []); // eslint-disable-line

  // ── Accessibility — Layer 1 (detect at app load) ──────────────────────────
  // Three-signal detection: URL override, screen reader, saved preference.
  // Toggle (rendered below) is the reliable fallback regardless.
  const accessibilityInitialise = useAccessibilityStore(s => s.initialise);
  const accessibilityHasInitialised = useAccessibilityStore(s => s.hasInitialised);
  useEffect(() => {
    if (accessibilityHasInitialised) return;
    detectAccessibilityNeeds({ token, apiBase: API }).then(detectorResult => {
      accessibilityInitialise({ token, apiBase: API, detectorResult });
    });
  }, [token, accessibilityHasInitialised, accessibilityInitialise]);

  const [channels, setChannels] = useState([]);
  const [myChannels, setMyChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [view, setView] = useState('my');
  const [filterCat, setFilterCat] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateInitiative, setShowCreateInitiative] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [thread, setThread] = useState(null); // root message id of the open thread (25 Oct 2026, backend #217)
  const [panel, setPanel] = useState(null); // 'notices' | 'appeals' | null — moderation with a right of appeal (25 Oct 2026)
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const headers = useCallback(() => ({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }), [token]);
  // Unread Signal notices — a count on the 🔔, refreshed on channel change.
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/notifications/count?app=signal`, { headers: headers() }).then((r) => r.json())
      .then((d) => { if (d.success) setUnread(d.unread || 0); }).catch(() => {});
  }, [token, activeChannel, headers]);

  const loadMyChannels = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/signal/channels/mine`, { headers: headers() });
      const data = await res.json();
      if (data.success) setMyChannels(data.data);
    } catch {}
  }, [token, headers]);

  const loadChannels = useCallback(async () => {
    if (!token) return;
    try {
      // Use personalised suggestions when on 'all' — Maia scores by user interests
      // Fall back to category filter when user wants a specific category
      const url = filterCat === 'all'
        ? `${API}/api/signal/suggestions`
        : `${API}/api/signal/channels?category=${filterCat}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (data.success) {
        // Mark unjoned channels so the Join button shows
        const joined = new Set((myChannels || []).map(c => c.slug));
        const marked = (data.data || []).map(ch => ({ ...ch, joined: joined.has(ch.slug) }));
        setChannels(marked);
      }
    } catch {}
  }, [token, filterCat, myChannels, headers]);

  const loadMessages = useCallback(async () => {
    if (!token || !activeChannel) return;
    try {
      const res = await fetch(`${API}/api/signal/channels/${activeChannel.slug}/messages`, { headers: headers() });
      const data = await res.json();
      if (data.success) setMessages(data.data);
    } catch {}
  }, [token, activeChannel, headers]);

  // Reload channel details to get fresh pinned items
  const reloadChannel = useCallback(async () => {
    if (!token || !activeChannel) return;
    try {
      const res = await fetch(`${API}/api/signal/channels?search=${encodeURIComponent(activeChannel.name)}`, { headers: headers() });
      const data = await res.json();
      if (data.success && data.data.length > 0) {
        const fresh = data.data.find(c => c.slug === activeChannel.slug);
        if (fresh) setActiveChannel(prev => ({ ...prev, ...fresh }));
      }
    } catch {}
  }, [token, activeChannel, headers]);

  useEffect(() => { loadMyChannels(); }, [loadMyChannels]);
  useEffect(() => { if (view === 'discover') loadChannels(); }, [view, loadChannels]);
  useEffect(() => {
    loadMessages();
    if (pollRef.current) clearInterval(pollRef.current);
    if (activeChannel) pollRef.current = setInterval(loadMessages, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeChannel, loadMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending || !activeChannel) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/signal/channels/${activeChannel.slug}/messages`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ content: input.trim() }),
      });
      const data = await res.json();
      if (data.success) { setMessages(prev => [...prev, data.data]); setInput(''); }
    } catch {}
    setSending(false);
  };

  const joinChannel = async (slug) => {
    try {
      await fetch(`${API}/api/signal/channels/${slug}/join`, { method: 'POST', headers: headers() });
      loadMyChannels(); loadChannels();
    } catch {}
  };

  const selectChannel = (ch) => { setActiveChannel(ch); setMessages([]); setShowActions(false); setThread(null); };
  const logout = () => { localStorage.removeItem('signal_token'); localStorage.removeItem('signal_user'); setToken(null); setUser(null); };

  if (!token) return (
    <div style={{ minHeight: '100dvh', background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AccessibilityToggle accentColor="#14b8a6" apiBase={API} />
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <NarbodicaAuth
          app="signal"
          appLabel="Signal"
          appTagline="You can't stop the signal"
          accentColor="#14b8a6"
          apiBase={API}
          onSuccess={(tokens, u) => {
            const t = tokens?.accessToken || tokens?.token;
            if (t) {
              localStorage.setItem('signal_token', t);
              if (tokens?.refreshToken) localStorage.setItem('signal_refresh', tokens.refreshToken);
              if (u) localStorage.setItem('signal_user', JSON.stringify(u));
              setToken(t);
              setUser(u);
            }
          }}
        />
      </div>
    </div>
  );

  // Onboarding gate — show NarbodicaProfileOnboarding before chat UI loads
  // when the user hasn't completed Signal onboarding yet. Writes to
  // NarbodicaProfile (the unified cross-app profile) so anything they
  // share here is also available to Maia and other apps. Per JEzza's
  // 29 Apr 2026 instruction: "all apps will have onboarding as we don't
  // want to force a user to sign up in an app they don't want/need".
  if (needsOnboarding && onboardingChecked) {
    return (
      <>
        <AccessibilityToggle accentColor="#14b8a6" token={token} apiBase={API} />
        <NarbodicaProfileOnboarding
          app="signal"
          appLabel="Signal"
          accentColor="#14b8a6"
          apiBase={API}
          token={token}
          onComplete={() => setNeedsOnboarding(false)}
        />
      </>
    );
  }

  // Quietly wait for the onboarding check to finish before rendering the
  // chat UI — prevents a flash of chat then redirect to onboarding.
  if (!onboardingChecked) {
    return (
      <div style={{ minHeight: '100dvh', background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#52525b', fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
        Loading…
      </div>
    );
  }

  const userId = user?._id || user?.id;
  // Channel moderators = the creator plus anyone in `moderators` (when the API sends it).
  // Admins too. The backend enforces this; here it only decides what to show.
  const canModerate = !!activeChannel && (
    String(activeChannel.createdBy) === String(userId)
    || (activeChannel.moderators || []).some((m) => String(m?._id || m) === String(userId))
    || ['admin', 'super_admin'].includes(user?.role)
  );

  const activePins = activeChannel?.pinnedItems?.filter(p => p.active !== false) || [];

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', background: '#09090b', color: '#e4e4e7', display: 'flex', fontFamily: "'Inter', sans-serif" }}>
      <AccessibilityToggle accentColor="#14b8a6" token={token} apiBase={API} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap" rel="stylesheet" />

      {/* Category bar */}
      <div style={{ width: 64, background: 'rgba(9,9,11,0.9)', borderRight: '1px solid rgba(255,255,255,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 4, overflowY: 'auto', flexShrink: 0 }}>
        {[
          { id: 'my', icon: '💬', tip: 'My channels' },
          { id: 'discover', icon: '🔍', tip: 'Discover' },
        ].map(v => (
          <div key={v.id} style={{ width: 40, height: 40, borderRadius: 12, background: view === v.id ? 'rgba(20,184,166,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', border: view === v.id ? '1px solid rgba(20,184,166,0.3)' : '1px solid transparent' }}
            onClick={() => setView(v.id)} title={v.tip}>{v.icon}</div>
        ))}
        <div style={{ width: 28, height: 1, background: '#27272a', margin: '4px 0' }} />
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(20,184,166,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', border: '1px solid rgba(20,184,166,0.15)' }}
          onClick={() => setShowCreate(true)} title="Create channel">+</div>
      </div>

      {/* Channel list */}
      <div style={{ width: 240, background: 'rgba(14,14,16,0.8)', borderRight: '1px solid rgba(255,255,255,0.04)', display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100dvh' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: 18, color: '#14b8a6' }}>Signal</div>
          <div style={{ fontSize: 10, color: '#3f3f46' }}>{view === 'my' ? 'Your channels' : 'Discover channels'}</div>
        </div>

        {view === 'discover' && (
          <div style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            {CATEGORIES.slice(0, 8).map(c => (
              <button key={c.id} onClick={() => setFilterCat(c.id)}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: `1px solid ${filterCat === c.id ? 'rgba(20,184,166,0.3)' : 'rgba(255,255,255,0.04)'}`, background: filterCat === c.id ? 'rgba(20,184,166,0.1)' : 'transparent', color: filterCat === c.id ? '#14b8a6' : '#52525b', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                {c.icon}
              </button>
            ))}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {(view === 'my' ? myChannels : channels).map(ch => {
            const hasPins = ch.pinnedItems?.some(p => p.active !== false);
            return (
              <button key={ch._id || ch.slug} onClick={() => { if (ch.joined !== false) selectChannel(ch); else joinChannel(ch.slug); }}
                style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, marginBottom: 3, cursor: 'pointer', fontFamily: "'Inter', sans-serif",
                  border: `1px solid ${activeChannel?.slug === ch.slug ? 'rgba(20,184,166,0.3)' : 'rgba(255,255,255,0.03)'}`,
                  background: activeChannel?.slug === ch.slug ? 'rgba(20,184,166,0.08)' : 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14 }}>{ch.icon || '💬'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#d4d4d8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {ch.name}
                      {hasPins && <span style={{ fontSize: 8, color: '#14b8a6' }}>📌</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#3f3f46' }}>{ch.memberCount || 0} members</div>
                  </div>
                  {view === 'discover' && ch.joined === false && (
                    <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 6, background: 'rgba(20,184,166,0.1)', color: '#14b8a6', border: '1px solid rgba(20,184,166,0.2)' }}>Join</span>
                  )}
                </div>
              </button>
            );
          })}
          {(view === 'my' ? myChannels : channels).length === 0 && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{view === 'my' ? '💬' : '🔍'}</div>
              <div style={{ fontSize: 11, color: '#52525b', marginBottom: 10 }}>
                {view === 'my' ? 'No channels yet' : 'No channels found'}
              </div>
              {view === 'my' && (
                <button onClick={() => setView('discover')} style={{
                  fontSize: 10, padding: '5px 12px', borderRadius: 8,
                  background: 'rgba(20,184,166,0.1)', color: '#14b8a6',
                  border: '1px solid rgba(20,184,166,0.2)', cursor: 'pointer'
                }}>Discover channels →</button>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#3f3f46', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.email}</span>
          <button onClick={logout} style={{ fontSize: 10, color: '#52525b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>Sign out</button>
        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100dvh' }}>
        {!activeChannel ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', maxWidth: 400 }}>
              <div style={{ fontFamily: "var(--font-serif)", fontSize: 28, color: '#14b8a6', marginBottom: 4 }}>Signal</div>
              <div style={{ fontSize: 12, color: '#52525b', lineHeight: 1.6, marginBottom: 20 }}>
                Find your people. Join a conversation. Or start one.<br />
                No algorithms. No ads. Just humans talking about the things that matter.
              </div>
              {myChannels.length === 0 && (
                <button onClick={() => setView('discover')} style={{
                  padding: '10px 22px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                  background: 'rgba(20,184,166,0.12)', color: '#14b8a6',
                  border: '1px solid rgba(20,184,166,0.25)', cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", marginBottom: 16,
                }}>Discover channels →</button>
              )}
              <div style={{ fontSize: 11, color: '#3f3f46', fontStyle: 'italic' }}>You can't stop the signal.</div>
            </div>
          </div>
        ) : (
          <>
            {/* Channel header with action buttons */}
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', flexShrink: 0 }}>
              <div style={{ height: 52, display: 'flex', alignItems: 'center', padding: '0 20px', gap: 10 }}>
                <span style={{ fontSize: 18 }}>{activeChannel.icon || '💬'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#d4d4d8' }}>{activeChannel.name}</div>
                  <div style={{ fontSize: 10, color: '#52525b' }}>{activeChannel.description || activeChannel.memberCount + ' members'}</div>
                </div>
                {activeChannel.deepThreads?.enabled && <DeepBadge minChars={activeChannel.deepThreads.minChars} />}
                {activeChannel.tone && activeChannel.tone !== 'casual' && (
                  <span style={{ fontSize: 9, padding: '3px 10px', borderRadius: 6, background: 'rgba(20,184,166,0.08)', color: '#14b8a6', border: '1px solid rgba(20,184,166,0.15)' }}>
                    {activeChannel.tone.replace('_', ' ')}
                  </span>
                )}
                {/* Action menu toggle */}
                <button onClick={() => setShowActions(!showActions)}
                  style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${showActions ? 'rgba(20,184,166,0.3)' : 'rgba(255,255,255,0.06)'}`, background: showActions ? 'rgba(20,184,166,0.08)' : 'transparent', color: '#71717a', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  +
                </button>
              </div>

              {/* Action bar — Create Event / Start Initiative */}
              {showActions && (
                <div style={{ padding: '8px 20px 10px', display: 'flex', gap: 8, borderTop: '1px solid rgba(255,255,255,0.03)' }}>
                  <button onClick={() => { setShowCreateEvent(true); setShowActions(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(20,184,166,0.2)', background: 'rgba(20,184,166,0.06)', color: '#14b8a6', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                    📅 Create event
                  </button>
                  <button onClick={() => { setPanel(panel === 'search' ? null : 'search'); setShowActions(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(20,184,166,0.2)', background: 'rgba(20,184,166,0.06)', color: '#14b8a6', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                    🔎 Search
                  </button>
                  <DigestToggle headers={headers} />
                  <button onClick={() => { setPanel(panel === 'notices' ? null : 'notices'); setShowActions(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(245,158,11,0.2)', background: 'rgba(245,158,11,0.06)', color: '#f59e0b', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                    🔔 Notices{unread > 0 ? ` (${unread})` : ''}
                  </button>
                  {canModerate && (
                    <button onClick={() => { setPanel(panel === 'appeals' ? null : 'appeals'); setShowActions(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(248,113,113,0.2)', background: 'rgba(248,113,113,0.06)', color: '#f87171', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                      ⚖️ Appeals
                    </button>
                  )}
                  {canModerate && (
                    <DeepToggle channel={activeChannel} headers={headers}
                      onChanged={(dt) => { setActiveChannel((c) => ({ ...c, deepThreads: dt })); loadMessages(); }} />
                  )}
                  <button onClick={() => { setShowCreateInitiative(true); setShowActions(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.06)', color: '#22c55e', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                    ✊ Start initiative
                  </button>
                </div>
              )}
            </div>

            {panel === 'notices' && <NoticesPanel headers={headers} onClose={() => { setPanel(null); setUnread(0); }} />}
            {panel === 'appeals' && <AppealsPanel headers={headers} onClose={() => setPanel(null)} />}
            {panel === 'search' && (
              <SearchPanel headers={headers} channel={activeChannel} onClose={() => setPanel(null)}
                onJump={(h) => {
                  // Jump: switch channel if needed (keeping the panel state tidy), then open the thread the hit lives in.
                  if (h.channel?.slug && h.channel.slug !== activeChannel?.slug) {
                    const ch = myChannels.find((c) => c.slug === h.channel.slug);
                    if (ch) { setActiveChannel(ch); setMessages([]); setShowActions(false); }
                  }
                  setPanel(null);
                  setThread(h.rootId);
                }} />
            )}

            {/* Pinned items */}
            {activePins.length > 0 && (
              <div style={{ padding: '8px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: 8, overflowX: 'auto', flexShrink: 0 }}>
                {activePins.map((pin, i) => {
                  const isEvent = pin.type === 'connect_event';
                  return (
                    <a key={i} href={pin.url || '#'} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 10, textDecoration: 'none', flexShrink: 0,
                        background: isEvent ? 'rgba(20,184,166,0.06)' : 'rgba(34,197,94,0.06)',
                        border: `1px solid ${isEvent ? 'rgba(20,184,166,0.15)' : 'rgba(34,197,94,0.15)'}` }}>
                      <span style={{ fontSize: 16 }}>{isEvent ? '📅' : '✊'}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#d4d4d8' }}>{pin.title}</div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>
                          {pin.description && <span>{pin.description.substring(0, 50)}</span>}
                          {pin.date && <span> — {new Date(pin.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 6, background: isEvent ? 'rgba(20,184,166,0.1)' : 'rgba(34,197,94,0.1)', color: isEvent ? '#14b8a6' : '#22c55e', border: `1px solid ${isEvent ? 'rgba(20,184,166,0.2)' : 'rgba(34,197,94,0.2)'}` }}>
                        {isEvent ? 'Event' : 'Initiative'}
                      </span>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Channel summary for context */}
            <ChannelSummary token={token} channel={activeChannel} />

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>👋</div>
                  <div style={{ fontSize: 13, color: '#52525b' }}>Welcome to #{activeChannel.name}</div>
                  <div style={{ fontSize: 11, color: '#3f3f46', marginTop: 4 }}>This is the beginning of the conversation.</div>
                </div>
              )}
              {messages.map(msg => (
                <ChatMessage key={msg._id} msg={msg} isOwn={msg.user?._id === userId || msg.user === userId}
                  canModerate={canModerate} headers={headers} onHidden={(id) => setMessages((ms) => ms.filter((m) => m._id !== id))}
                  onOpenThread={setThread} />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.04)', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder={`Message #${activeChannel.name}...`}
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(39,39,42,0.5)', color: '#e4e4e7', fontSize: 13, outline: 'none', fontFamily: "'Inter', sans-serif" }}
                />
                <button onClick={sendMessage} disabled={sending || !input.trim()}
                  style={{ padding: '10px 18px', borderRadius: 10, border: '1px solid rgba(20,184,166,0.3)', background: 'rgba(20,184,166,0.1)', color: '#14b8a6', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif", opacity: !input.trim() ? 0.4 : 1 }}>
                  Send
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Thread panel — the channel shows roots only; the debate lives here */}
      {thread && activeChannel && (
        <ThreadPanel rootId={thread} headers={headers} userId={userId} deep={activeChannel.deepThreads} onClose={() => setThread(null)}
          onReplied={(id, count, at) => setMessages((ms) => ms.map((m) => (m._id === id ? { ...m, replyCount: count, lastReplyAt: at } : m)))} />
      )}

      {/* Modals */}
      {showCreate && <CreateChannelModal token={token} onCreated={(ch) => { loadMyChannels(); selectChannel(ch); }} onClose={() => setShowCreate(false)} />}
      {showCreateEvent && activeChannel && <CreateEventModal token={token} channel={activeChannel} onCreated={() => { reloadChannel(); loadMessages(); }} onClose={() => setShowCreateEvent(false)} />}
      {showCreateInitiative && activeChannel && <CreateInitiativeModal token={token} channel={activeChannel} onCreated={() => { reloadChannel(); loadMessages(); }} onClose={() => setShowCreateInitiative(false)} />}

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #27272a; border-radius: 2px; }
        select option { background: #18181b; color: #e4e4e7; }
      `}</style>
    </div>
  );
}
